import { unzip } from "es-toolkit";
import { runInAction } from "mobx";

import { deleteNode } from "@/input/utils/editOperators";
import { cursorIsActive } from "@/models/CursorModel";
import { FunctionNodeModel } from "@/models/nodes/FunctionNodeModel";
import { GroupNodeModel } from "@/models/nodes/GroupNodeModel";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { MatrixNodeModel } from "@/models/nodes/MatrixNodeModel";
import { NumberNodeModel } from "@/models/nodes/NumberNodeModel";
import { VariableNodeModel } from "@/models/nodes/VariableNodeModel";

import type { CursorModel } from "@/models/CursorModel";
import type { ActiveMatrixCursor, MatrixCell } from "@/models/nodes/MatrixNodeModel";
import type { NodeModel } from "@/models/nodes/types";
import type { CellPosition, MatrixBounds } from "@corca/data";

export function isMatrixCell(node?: NodeModel): node is MatrixCell {
  return !!node?.parent?.is(MatrixNodeModel);
}

export function isMatrixLikeNode(node?: NodeModel) {
  return !!(node?.is(VariableNodeModel) || node?.is(FunctionNodeModel));
}

export function isMatrix(node?: NodeModel) {
  if (node?.is(GroupNodeModel) && node.matrixNode) {
    return hasMatrixBounds(node.matrixNode.bounds);
  }

  return false;
}

export function isActiveMatrixCell(cursor?: CursorModel): cursor is ActiveMatrixCursor {
  if (cursor?.node) {
    return cursorIsActive(cursor) && isMatrixCell(cursor.node);
  }

  return false;
}

export function hasGroupBounds({ rows, cols }: MatrixBounds) {
  return rows === 1 && cols === 1;
}

export function hasVerticalVectorBounds({ rows, cols }: MatrixBounds) {
  return cols === 1 && rows > 1;
}

export function hasHorizontalVectorBounds({ rows, cols }: MatrixBounds) {
  return rows === 1 && cols > 1;
}

export function hasVectorBounds(bounds: MatrixBounds) {
  return hasVerticalVectorBounds(bounds) || hasHorizontalVectorBounds(bounds);
}

export function hasMatrixBounds({ rows, cols }: MatrixBounds) {
  return rows > 1 && cols > 1;
}

export function hasEqualMatrixBounds(bounds: MatrixBounds) {
  return hasMatrixBounds(bounds) && bounds.rows === bounds.cols;
}

export function canConvertToMatrix(node?: InlineNodeModel) {
  return !isMatrixCell(node) && isMatrixLikeNode(node?.parent);
}

export function createMatrixFrom(node: InlineNodeModel, bounds: MatrixBounds) {
  const { doc } = node;

  doc.transact(() => {
    runInAction(() => {
      const matrix = doc.create(MatrixNodeModel, bounds, [doc.create(InlineNodeModel, node.nodes)]);

      node.setChildren([matrix]);

      if (cursorIsActive(doc.cursor)) {
        doc.cursor.moveTo(matrix.nodes[1], 0);
      }
    });
  }, "matrix:createFrom");
}

export function canConvertToInline(node: MatrixNodeModel) {
  const inline = node.ancestors.find((n) => n.is(InlineNodeModel));

  if (isMatrixLikeNode(inline?.parent)) {
    return node.bounds.cols === 1 && node.bounds.rows === 1 && node.nodes.length === 1;
  }

  return false;
}

export function replaceMatrixWithCellAtPosition(node: MatrixNodeModel, position: CellPosition) {
  const cell = node.getCellNodeByPosition(position);
  const { doc, parent, index } = node;

  if (!cell) {
    return;
  }

  doc.transact(() => {
    deleteNode(node);

    if (!cell.isEmpty && parent) {
      cell.nodes.forEach((n, i) => {
        const nodeAfter = parent.nodes[index + i];

        if (nodeAfter) {
          nodeAfter.insertSiblingBefore(n);
        } else {
          parent.appendChild(n);
        }
      });
    }
  }, "matrix:replaceWithCell");

  if (parent?.is(InlineNodeModel)) {
    queueMicrotask(() => {
      doc.cursor.moveTo(parent, index);
    });
  }
}

/**
 * Inserts matrix column relatively to cell position.
 */
export function insertColumn(node: MatrixCell) {
  const { doc, parent: matrixNode } = node;
  const position = matrixNode.getCellPosition(node);

  if (!position) {
    return;
  }

  doc.transact(() => {
    preserveCursorPosition(
      matrixNode,
      () => {
        matrixNode.insertColumn(position.col + 1);
      },
      1,
    );
  }, "matrix:insertColumn");
}

/**
 * Inserts matrix row relatively to cell position.
 */
export function insertRow(node: MatrixCell) {
  const { doc, parent: matrixNode } = node;
  const position = matrixNode.getCellPosition(node);

  if (!position) {
    return;
  }

  doc.transact(() => {
    preserveCursorPosition(
      matrixNode,
      () => {
        matrixNode.insertRow(position.row + 1);
      },
      0,
      1,
    );
  }, "matrix:insertRow");
}

export function flipMatrix(node: MatrixNodeModel, dir: "horizontal" | "vertical") {
  const { doc } = node;

  doc.transact(() => {
    runInAction(() => {
      switch (dir) {
        case "horizontal": {
          const newMatrix = node.matrix.map((row) => row.slice().reverse());
          node.setChildren(newMatrix.flat());
          break;
        }

        case "vertical": {
          const newMatrix = node.matrix.slice().reverse();
          node.setChildren(newMatrix.flat());
          break;
        }

        default: {
          break;
        }
      }
    });
  }, `matrix:flip:${dir}`);
}

export function transpose(node: MatrixNodeModel) {
  const { doc } = node;

  doc.transact(() => {
    runInAction(() => {
      const transposed = unzip(node.matrix);
      const rows = transposed.length;
      const cols = transposed[0].length;

      node.setBounds({ rows, cols });
      node.setChildren(transposed.flat());
    });
  }, "matrix:transpose");
}

export function rotate(node: MatrixNodeModel, dir: "left" | "right") {
  const { doc } = node;

  doc.transact(() => {
    runInAction(() => {
      let transposed = unzip(node.matrix);

      transposed =
        dir === "left"
          ? transposed.slice().reverse()
          : transposed.map((row) => row.slice().reverse());

      const rows = transposed.length;
      const cols = transposed[0].length;

      node.setBounds({ rows, cols });
      node.setChildren(transposed.flat());
    });
  }, "matrix:rotate");
}

export function toIdentityMatrix(node: MatrixNodeModel) {
  const { doc } = node;

  doc.transact(() => {
    runInAction(() => {
      node.matrix.forEach((row, rowIndex) => {
        row.forEach((cell, cellIndex) => {
          const value = rowIndex === cellIndex ? "1" : "0";
          cell.setChildren([doc.create(NumberNodeModel, value)]);
        });
      });
    });
  }, "matrix:toIdentityMatrix");
}

export function preserveCursorPosition(
  node: MatrixNodeModel,
  cb: () => void,
  deltaX = 0,
  deltaY = 0,
) {
  const { doc } = node;
  const position = node.getCellPosition(doc.cursor.node);

  cb();

  if (!position) {
    return;
  }

  const x = Math.max(0, position.col + deltaX);
  const y = Math.max(0, position.row + deltaY);

  const col = Math.min(x, node.cols - 1);
  const row = Math.min(y, node.rows - 1);

  const cell = node.getCellNodeByPosition({ col, row });

  if (cell) {
    /**
     * Callback `cb` could invalidate cursor position hence we call the `doc.cursor.reset() right after`.
     * `queueMicrotask` ensures that the cursor repositioning happens after the updates triggered by cb() and `doc.input.reset` have completed.
     */
    queueMicrotask(() => {
      doc.cursor.moveTo(cell, cell.nodes.length);
    });
  }
}

export function shouldMoveOutOfMatrix(
  cursor: CursorModel,
  direction: "left" | "right",
): cursor is ActiveMatrixCursor {
  if (!isActiveMatrixCell(cursor)) {
    return false;
  }

  const {
    node,
    node: { parent: matrixNode },
  } = cursor;

  if (matrixNode.rows === 1) {
    return false;
  }

  const position = matrixNode.getCellPosition(node);

  if (!position) {
    return false;
  }

  if (direction === "left") {
    return cursor.index === 0 && position.col === 0;
  }

  if (direction === "right") {
    return cursor.index === node.nodes.length && position.col === matrixNode.cols - 1;
  }

  return false;
}

export function shouldMoveToMatrix(cursor: CursorModel, direction: "left" | "right") {
  const matrixNode = getCursorMatrix(cursor, direction);
  return !!(matrixNode && matrixNode.rows > 1);
}

export function moveToMatrix(cursor: CursorModel, direction: "left" | "right") {
  const matrixNode = getCursorMatrix(cursor, direction);

  if (!matrixNode) {
    return false;
  }

  const targetCell = matrixNode.getCellNodeByPosition({
    col: direction === "left" ? matrixNode.cols - 1 : 0,
    row: Math.floor(matrixNode.rows / 2) - (matrixNode.rows % 2 === 0 ? 1 : 0),
  });

  cursor.moveTo(targetCell, direction === "left" ? (targetCell?.nodes.length ?? 0) : 0);
}

export function moveOutOfMatrix(cursor: ActiveMatrixCursor, direction: "left" | "right") {
  const { parent: matrixNode } = cursor.node;
  let targetNode: NodeModel = matrixNode;

  if (matrixNode.parent?.is(GroupNodeModel)) {
    targetNode = matrixNode.parent;
  }

  if (direction === "left") {
    cursor.moveBeforeNode(targetNode);
  } else {
    cursor.moveAfterNode(targetNode);
  }

  return true;
}

function getCursorMatrix(
  cursor: CursorModel,
  direction: "left" | "right",
): MatrixNodeModel | undefined {
  if (!cursorIsActive(cursor)) {
    return undefined;
  }

  const node = direction === "left" ? cursor.nodeBeforeCursor : cursor.nodeAfterCursor;

  if (node?.is(MatrixNodeModel)) {
    return node;
  }

  if (node?.is(GroupNodeModel)) {
    return node.matrixNode;
  }

  return undefined;
}

export function insertMatrixIntoMatrix(
  sourceMatrix: MatrixNodeModel,
  targetMatrix: MatrixNodeModel,
  targetIndex: number,
): MatrixNodeModel {
  const sourceRows = sourceMatrix.rows;
  const sourceCols = sourceMatrix.cols;

  if (sourceRows === 0 || sourceCols === 0) {
    return targetMatrix;
  }

  const { rows, cols } = targetMatrix;

  const index = Math.max(0, targetIndex);
  const startRow = Math.floor(index / cols);
  const startCol = index % cols;

  const requiredRows = startRow + sourceRows;
  const requiredCols = startCol + sourceCols;

  const newRows = Math.max(rows, requiredRows);
  const newCols = Math.max(cols, requiredCols);

  runInAction(() => {
    if (newRows !== rows || newCols !== cols) {
      targetMatrix.setBounds({ rows: newRows, cols: newCols });
    }

    const targetGrid = targetMatrix.matrix.map((row) => row.slice());
    const sourceGrid = sourceMatrix.matrix;

    for (let r = 0; r < sourceRows; r++) {
      for (let c = 0; c < sourceCols; c++) {
        const tr = startRow + r;
        const tc = startCol + c;

        if (tr < targetGrid.length && tc < targetGrid[tr].length) {
          targetGrid[tr][tc] = sourceGrid[r][c];
        }
      }
    }

    targetMatrix.setChildren(targetGrid.flat());

    const lastInsertedCell = targetMatrix.matrix[requiredRows - 1][requiredCols - 1];
    targetMatrix.doc.cursor.moveTo(lastInsertedCell, lastInsertedCell.nodes.length);
  });

  return targetMatrix;
}
